/**
 * Quotation Number Generator Utility
 * Based on invoice number generator but for quotations
 */

/**
 * Generates a unique quotation number based on company name, existing quotations, and date
 * Format: QUO-[COMPANY_CODE]-YYYYMM-[SEQUENCE]
 * 
 * @param {string} companyName - Name of the company
 * @param {Array} existingQuotations - Array of existing quotations with quotationNo field
 * @param {Date} date - Date for which to generate the quotation number (defaults to current date)
 * @returns {string} Generated quotation number
 */
export function generateQuotationNumber(companyName, existingQuotations = [], date = new Date()) {
  if (!companyName || typeof companyName !== 'string') {
    console.warn('Invalid company name provided for quotation number generation');
    return `QUO-${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}-001`;
  }

  // Generate company code from company name (first 3 letters, uppercase)
  const companyCode = companyName
    .replace(/[^a-zA-Z\s]/g, '') // Remove non-alphabetic characters except spaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .substring(0, 3)
    .padEnd(3, 'X'); // If less than 3 characters, pad with 'X'

  // Format date as YYYYMM
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const datePrefix = `${year}${month}`;

  // Generate the prefix for this quotation
  const quotationPrefix = `QUO-${companyCode}-${datePrefix}-`;

  // Find existing quotations with the same prefix
  const sameMonthQuotations = existingQuotations.filter(quotation => 
    quotation.quotationNo && 
    typeof quotation.quotationNo === 'string' && 
    quotation.quotationNo.startsWith(quotationPrefix)
  );

  // Extract sequence numbers and find the highest one
  let highestSequence = 0;
  
  sameMonthQuotations.forEach(quotation => {
    const sequencePart = quotation.quotationNo.substring(quotationPrefix.length);
    const sequenceNumber = parseInt(sequencePart, 10);
    
    if (!isNaN(sequenceNumber) && sequenceNumber > highestSequence) {
      highestSequence = sequenceNumber;
    }
  });

  // Generate next sequence number
  const nextSequence = highestSequence + 1;
  const paddedSequence = String(nextSequence).padStart(3, '0');

  // Return the generated quotation number
  const quotationNumber = `${quotationPrefix}${paddedSequence}`;
  
  console.log('Generated quotation number:', {
    companyName,
    companyCode,
    datePrefix,
    existingCount: sameMonthQuotations.length,
    nextSequence,
    quotationNumber
  });

  return quotationNumber;
}

/**
 * Validates if a quotation number follows the expected format
 * @param {string} quotationNo - Quotation number to validate
 * @returns {boolean} Whether the quotation number is valid
 */
export function validateQuotationNumber(quotationNo) {
  if (!quotationNo || typeof quotationNo !== 'string') {
    return false;
  }

  // Check if it follows the pattern: QUO-XXX-YYYYMM-NNN
  const pattern = /^QUO-[A-Z]{3}-\d{6}-\d{3}$/;
  return pattern.test(quotationNo);
}

/**
 * Extracts information from a quotation number
 * @param {string} quotationNo - Quotation number to parse
 * @returns {Object} Parsed quotation number information
 */
export function parseQuotationNumber(quotationNo) {
  if (!validateQuotationNumber(quotationNo)) {
    return null;
  }

  const parts = quotationNo.split('-');
  
  if (parts.length !== 4) {
    return null;
  }

  const [prefix, companyCode, datePart, sequence] = parts;
  const year = parseInt(datePart.substring(0, 4), 10);
  const month = parseInt(datePart.substring(4, 6), 10);

  return {
    prefix,
    companyCode,
    year,
    month,
    sequence: parseInt(sequence, 10),
    dateString: `${year}-${String(month).padStart(2, '0')}`
  };
}

/**
 * Gets the next suggested quotation number for a given company and date
 * @param {string} companyName - Name of the company
 * @param {Array} existingQuotations - Array of existing quotations
 * @param {Date} date - Date for the quotation (optional, defaults to current date)
 * @returns {string} Suggested quotation number
 */
export function getNextQuotationNumber(companyName, existingQuotations = [], date = new Date()) {
  return generateQuotationNumber(companyName, existingQuotations, date);
}

const quotationNumberGenerator = {
  generateQuotationNumber,
  validateQuotationNumber,
  parseQuotationNumber,
  getNextQuotationNumber
};

export default quotationNumberGenerator;